// Build a routine-name → declared-return-type resolver from the schema-bus
// snapshot routines (A3). Feeds `inferExpressionResultType` so a single
// user-defined-function call (`my_total(x) ▮`) is typed like a built-in, which
// lets the operator suggester prune type-invalid operators after it.
//
// Accepts the minimal shape `{ name, returns? }` so each engine resolver can
// pass its (narrower) `SnapshotRoutine[]` directly — the per-engine projections
// omit `returns` in their local type, but the underlying bus `RoutineMeta`
// carries it at runtime, and an optional field keeps the projection assignable.

import type { ResolveRoutineReturnType } from '@/features/sql-resolver/structure/inferExpressionResultType';

export type RoutineReturnShape = {
  readonly name: string;
  readonly returns?: string;
};

const NO_ROUTINE: ResolveRoutineReturnType = () => undefined;

export function makeRoutineReturnResolver(
  routines: readonly RoutineReturnShape[],
  fold: (s: string) => string,
): ResolveRoutineReturnType {
  if (routines.length === 0) return NO_ROUTINE;
  const byName = new Map<string, string>();
  for (const r of routines) {
    if (r.returns) byName.set(fold(r.name), r.returns);
  }
  if (byName.size === 0) return NO_ROUTINE;
  // Strip a schema/table qualifier — the call's leading identifier may be
  // `schema.fn`; we key on the bare routine name (first-match across schemas,
  // matching the conservative resolution posture elsewhere).
  return (functionName: string): string | undefined => {
    const folded = fold(functionName);
    const bare = folded.includes('.') ? folded.slice(folded.lastIndexOf('.') + 1) : folded;
    return byName.get(bare);
  };
}
